import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput, DateTimeInput } from "@/components/DateInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { formatApiErrorDetail, toInputDate, toInputDateTime } from "@/lib/format";
import { DdtLines, emptyRiga } from "@/components/ddt/DdtLines";

const CAUSALI = ["Vendita", "Reso", "Conto lavorazione", "Conto visione", "Trasferimento a cantiere", "Comodato d'uso", "Riparazione", "Sostituzione in garanzia", "Omaggio"];
const ASPETTO = ["A vista", "Sfuso", "Cartoni", "Pallet / bancali", "Sacchi", "Fasci", "Rotoli"];

const blank = () => ({
  progressivo: "", tipo: "consegna", data: toInputDate(new Date().toISOString()), contact_id: "", destinatario_nome: "",
  destinatario_indirizzo: "", destinatario_piva: "", luogo_destinazione: "", causale: "Vendita", trasporto_a_cura: "mittente",
  vettore: "", data_ritiro: "", colli: "", peso_kg: "", aspetto_beni: "A vista", porto: "franco", righe: [emptyRiga()],
  note: "", quote_id: null, quote_numero: "",
});

const fromDoc = (d) => ({
  ...blank(), ...d, data: toInputDate(d.data) || blank().data, data_ritiro: toInputDateTime(d.data_ritiro),
  colli: d.colli ?? "", peso_kg: d.peso_kg ?? "", progressivo: d.progressivo ?? "", righe: d.righe?.length ? d.righe : [emptyRiga()],
});

const F = ({ label, children, className = "" }) => <div className={`space-y-1.5 ${className}`}><Label className="text-xs">{label}</Label>{children}</div>;

const toPayload = (f) => ({
  ...f, progressivo: f.progressivo ? Number(f.progressivo) : null, data: f.data ? `${f.data}T12:00:00Z` : "",
  data_ritiro: f.data_ritiro ? new Date(f.data_ritiro).toISOString() : "", colli: f.colli === "" ? null : Number(f.colli),
  peso_kg: f.peso_kg === "" ? null : Number(f.peso_kg), contact_id: f.contact_id || null,
  righe: f.righe.map((r) => ({ ...r, quantita: Number(r.quantita) || 0 })),
});

export default function DdtBuilder({ open, onClose, onSaved, initial, contacts = [], quotes = [], importQuoteId }) {
  const [f, setF] = useState(blank());
  const [nextNum, setNextNum] = useState("");
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const edit = !!initial?.id;
  const set = (patch) => setF((x) => ({ ...x, ...patch }));
  const year = (f.data || "").slice(0, 4);

  const importQuote = async (qid) => {
    setImporting(true);
    try {
      const { data } = await api.get(`/ddt/from-quote/${qid}`);
      setF((x) => ({
        ...x, contact_id: data.contact_id || "", destinatario_nome: data.destinatario_nome || x.destinatario_nome,
        destinatario_indirizzo: data.destinatario_indirizzo || x.destinatario_indirizzo, destinatario_piva: data.destinatario_piva || x.destinatario_piva,
        righe: data.righe.length ? data.righe : x.righe, quote_id: data.quote_id, quote_numero: data.quote_numero,
      }));
      toast.success(`Importate ${data.righe.length} righe dal preventivo N. ${data.quote_numero}${data.escluse ? ` (${data.escluse} di manodopera escluse)` : ""}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  useEffect(() => {
    if (!open) return;
    setF(initial ? fromDoc(initial) : blank());
    if (importQuoteId) importQuote(importQuoteId);
  }, [open, initial, importQuoteId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open && /^\d{4}$/.test(year)) api.get(`/ddt/next-number?anno=${year}`).then((r) => setNextNum(r.data.numero)).catch(() => setNextNum(""));
  }, [open, year]);

  const pickContact = (id) => {
    const c = contacts.find((x) => x.id === id);
    set({ contact_id: id, destinatario_nome: c?.ragione_sociale || f.destinatario_nome, destinatario_indirizzo: c?.indirizzo || f.destinatario_indirizzo, destinatario_piva: c?.piva || f.destinatario_piva });
  };
  const setTipo = (tipo) => set({ tipo, causale: tipo === "reso" && f.causale === "Vendita" ? "Reso" : tipo === "consegna" && f.causale === "Reso" ? "Vendita" : f.causale });

  const save = async () => {
    setSaving(true);
    try {
      const r = edit ? await api.put(`/ddt/${initial.id}`, toPayload(f)) : await api.post("/ddt", toPayload(f));
      toast.success(edit ? `DDT N. ${r.data.numero} aggiornato` : `DDT N. ${r.data.numero} creato`);
      onSaved(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="ddt-builder-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{edit ? `Modifica DDT N. ${initial.numero}` : "Nuovo documento di trasporto"}</DialogTitle>
          <DialogDescription>Compila mittente, destinatario, trasporto e beni. La numerazione riparte da 1 ogni anno.</DialogDescription>
        </DialogHeader>
        <datalist id="ddt-causali">{CAUSALI.map((c) => <option key={c} value={c} />)}</datalist>
        <datalist id="ddt-aspetto">{ASPETTO.map((c) => <option key={c} value={c} />)}</datalist>
        <div className="space-y-5">
          <div className="grid sm:grid-cols-3 gap-3">
            <F label="Tipo documento">
              <Select value={f.tipo} onValueChange={setTipo}>
                <SelectTrigger data-testid="ddt-type-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="consegna">Consegna</SelectItem><SelectItem value="reso">Reso</SelectItem></SelectContent>
              </Select>
            </F>
            <F label={`Numero (${year || "anno"})`}>
              <Input type="number" min="1" value={f.progressivo} onChange={(e) => set({ progressivo: e.target.value })} placeholder={edit ? "" : `Automatico (${nextNum || "…"})`} data-testid="ddt-number-input" />
            </F>
            <F label="Data documento"><DateInput value={f.data} onChange={(e) => set({ data: e.target.value })} data-testid="ddt-date-input" /></F>
          </div>

          <div className="rounded-lg border border-dashed border-primary/40 bg-primary/5 p-3 flex flex-col sm:flex-row sm:items-center gap-2">
            <div className="text-sm flex-1">Importa cliente e righe da un preventivo <span className="text-muted-foreground">(senza prezzi)</span>{f.quote_numero && <b className="ml-1">· collegato al N. {f.quote_numero}</b>}</div>
            <Select value="" onValueChange={importQuote} disabled={importing}>
              <SelectTrigger className="sm:w-72 bg-card" data-testid="ddt-import-quote-select"><SelectValue placeholder={importing ? "Importo…" : "Scegli preventivo…"} /></SelectTrigger>
              <SelectContent>{quotes.map((q) => <SelectItem key={q.id} value={q.id}>N. {q.numero} · {q.cliente_nome}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <F label="Destinatario dalla rubrica">
              <Select value={f.contact_id || ""} onValueChange={pickContact}>
                <SelectTrigger data-testid="ddt-contact-select"><SelectValue placeholder="Seleziona (opzionale)" /></SelectTrigger>
                <SelectContent>{contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.ragione_sociale}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Destinatario *"><Input value={f.destinatario_nome} onChange={(e) => set({ destinatario_nome: e.target.value })} data-testid="ddt-recipient-input" /></F>
            <F label="Indirizzo destinatario"><Input value={f.destinatario_indirizzo || ""} onChange={(e) => set({ destinatario_indirizzo: e.target.value })} data-testid="ddt-recipient-address-input" /></F>
            <F label="P.IVA / Codice fiscale"><Input value={f.destinatario_piva || ""} onChange={(e) => set({ destinatario_piva: e.target.value })} data-testid="ddt-recipient-vat-input" /></F>
            <F label="Luogo di destinazione (cantiere)" className="sm:col-span-2"><Input value={f.luogo_destinazione || ""} onChange={(e) => set({ luogo_destinazione: e.target.value })} placeholder="Lascia vuoto se è l'indirizzo del destinatario" data-testid="ddt-destination-input" /></F>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <F label="Causale del trasporto" className="col-span-2"><Input list="ddt-causali" value={f.causale} onChange={(e) => set({ causale: e.target.value })} data-testid="ddt-reason-input" /></F>
            <F label="Trasporto a cura del">
              <Select value={f.trasporto_a_cura} onValueChange={(v) => set({ trasporto_a_cura: v })}>
                <SelectTrigger data-testid="ddt-transport-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="mittente">Mittente</SelectItem><SelectItem value="destinatario">Destinatario</SelectItem><SelectItem value="vettore">Vettore</SelectItem></SelectContent>
              </Select>
            </F>
            <F label="Porto">
              <Select value={f.porto} onValueChange={(v) => set({ porto: v })}>
                <SelectTrigger data-testid="ddt-port-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="franco">Franco</SelectItem><SelectItem value="assegnato">Assegnato</SelectItem></SelectContent>
              </Select>
            </F>
            {f.trasporto_a_cura === "vettore" && <F label="Vettore (ragione sociale, indirizzo, P.IVA)" className="col-span-2 sm:col-span-4"><Input value={f.vettore || ""} onChange={(e) => set({ vettore: e.target.value })} data-testid="ddt-carrier-input" /></F>}
            <F label="Aspetto dei beni"><Input list="ddt-aspetto" value={f.aspetto_beni || ""} onChange={(e) => set({ aspetto_beni: e.target.value })} data-testid="ddt-appearance-input" /></F>
            <F label="N. colli"><Input type="number" min="0" value={f.colli} onChange={(e) => set({ colli: e.target.value })} data-testid="ddt-packages-input" /></F>
            <F label="Peso (kg)"><Input type="number" min="0" step="any" value={f.peso_kg} onChange={(e) => set({ peso_kg: e.target.value })} data-testid="ddt-weight-input" /></F>
            <F label="Inizio trasporto"><DateTimeInput value={f.data_ritiro || ""} onChange={(e) => set({ data_ritiro: e.target.value })} data-testid="ddt-pickup-input" /></F>
          </div>

          <DdtLines righe={f.righe} onChange={(righe) => set({ righe })} />
          <F label="Annotazioni"><Textarea value={f.note || ""} onChange={(e) => set({ note: e.target.value })} rows={2} data-testid="ddt-notes-input" /></F>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving || !f.destinatario_nome} data-testid="ddt-save-button">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva DDT"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
